import React, { useState } from "react";
import {
  Calendar,
  Plus,
  FileText,
  Play,
  ChevronLeft,
  ChevronRight,
  Trash,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DashboardBookingModal } from "./DashboardBookingModal";
import {
  Appointment,
  useAppointmentsState,
} from "@/lib/dashboard/appointmentsData";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import PatientBookingModal from "@/components/PatientConsultation/components/PatientTemplateDEMDx/components/PatientBookingModal";
import { getPatientAppointmentsByPatientId } from "@/dispatchers/patientDispatchers";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { getVisiblePages } from "@/lib/utils";
import {
  setShowBookingSlots,
  setBookingSlotsMode,
} from "@/dispatchers/scheduleEventsDispatchers";
import { setShowPermissionModal,setShowPermissionMessage } from "@/dispatchers/appDispatchers";
import PermissionRequiredModal from "@/components/PermissionRequiredModal";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import { accessTitanverseCalenderUpsert,accessTitanverseConsultationUpsert } from "@/data/constantStrings";
interface UpcomingAppointmentsProps {
  appointments: Appointment[];
  pharmacists: any[];
  isDarkMode: boolean;
  onSaveBooking: (bookingData: any) => void;
  hasActiveConsultation?: boolean;
  onStartConsultation?: () => void;
  showAppointmentDeleteModal?: (value: boolean) => void;
  onDeleteAppointmentID?: (value: string) => void;
}

export function UpcomingAppointments({
  appointments,
  pharmacists,
  isDarkMode,
  onSaveBooking,
  hasActiveConsultation = false,
  onStartConsultation,
  showAppointmentDeleteModal,
  onDeleteAppointmentID,
}: UpcomingAppointmentsProps) {
  const [openBookingDialog, setOpenBookingDialog] = useState(false);
  const {
    patientAppointmentListLoader,
    selectedPatientDetails,
    handleStartConsultation,
    paginatedAppointments,
    totalPages,
    currentPage,
    itemsPerPage,
    handlePageChange,
  } = useAppointmentsState();
 const hasConsultationPermission = checkUserPermissions(
    accessTitanverseConsultationUpsert,
  );

  if (patientAppointmentListLoader) {
    return (
      <div
        className={`rounded-xl p-4 transition-colors duration-300 ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        } shadow-sm`}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar
              className={`w-4 h-4 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            />
            <h3
              className={`text-sm font-semibold transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Upcoming Appointments
            </h3>
          </div>
        </div>

        {/* Skeleton loader cards */}
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Box
              key={i}
              className={`border rounded-lg p-3 ${
                isDarkMode ? "border-gray-600" : "border-gray-200"
              }`}
            >
              <div className="flex items-start gap-3">
                <Skeleton
                  variant="rectangular"
                  width={48}
                  height={48}
                  className="rounded-lg"
                />
                <div className="flex-1 space-y-2">
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={16}
                    className="rounded"
                  />
                  <Skeleton
                    variant="text"
                    width="40%"
                    height={14}
                    className="rounded"
                  />
                </div>
              </div>
            </Box>
          ))}
        </div>
      </div>
    );
  }

  const handleCloseBookingDialog = () => {
    setOpenBookingDialog(false);
  };

  return (
    <div
      className={`rounded-xl p-4 transition-colors duration-300 ${
        isDarkMode ? "bg-gray-800" : "bg-white"
      } shadow-sm`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar
            className={`w-4 h-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          />
          <h3
            className={`text-sm font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Upcoming Appointments
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="secondary"
            className={`text-xs ${
              isDarkMode
                ? "bg-gray-700 text-gray-300"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {appointments.length}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            className="w-6 h-6 p-0"
            title="Add Appointment"
            onClick={() => {
              if (!checkUserPermissions(accessTitanverseCalenderUpsert)) {
                setShowPermissionModal(true);
                return;
              }
              setBookingSlotsMode("fromAppointment");
              setShowBookingSlots(true);
            }}
          >
            <Plus className="w-3 h-3" />
          </Button>
          <PatientBookingModal
            show={openBookingDialog}
            handleClose={handleCloseBookingDialog}
            patientName={
              `${selectedPatientDetails?.foreName || ""} ${
                selectedPatientDetails?.surName || ""
              }`.trim() ||
              selectedPatientDetails?.patientName ||
              ""
            }
            patientId={String(
              selectedPatientDetails?.patientID ||
                selectedPatientDetails?.patientId ||
                "",
            )}
            email={selectedPatientDetails?.email}
            mobile={
              selectedPatientDetails?.mobile ||
              selectedPatientDetails?.contacts?.find((c) => !!c.value)?.value ||
              ""
            }
            isDarkMode={isDarkMode}
            shouldRefreshAppointments={true}
            onRefreshAppointments={(id) =>
              getPatientAppointmentsByPatientId(id)
            }
          />
        </div>
      </div>

      <div className="space-y-3">
        {paginatedAppointments.map((appointment) => {
          const [day, month, year] = appointment.date.split("/");
          const fullYear =
            parseInt(year) < 50 ? 2000 + parseInt(year) : 1900 + parseInt(year);
          const appointmentDate = new Date(
            fullYear,
            parseInt(month) - 1,
            parseInt(day),
          );
          const today = new Date();
          const isToday =
            appointmentDate.toDateString() === today.toDateString();
          const timeDiff = appointmentDate.getTime() - today.getTime();
          const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
          const dayAbbr = appointmentDate.toLocaleDateString("en-US", {
            weekday: "short",
          });
          const dateNumber = appointmentDate.getDate();

          return (
            <div
              key={appointment.id}
              className={`border rounded-lg p-3 transition-colors duration-300 ${
                isToday
                  ? isDarkMode
                    ? "border-blue-500 bg-blue-900/20"
                    : "border-blue-300 bg-blue-50"
                  : isDarkMode
                    ? "border-gray-600"
                    : "border-gray-200"
              } group`}
            >
              <div className="flex items-start gap-3">
                {/* Calendar Date Box */}
                <div
                  className={`flex-shrink-0 w-12 h-12 rounded-lg flex flex-col items-center justify-center transition-colors duration-300 ${
                    isToday
                      ? isDarkMode
                        ? "bg-blue-600 text-white"
                        : "bg-blue-500 text-white"
                      : isDarkMode
                        ? "bg-gray-700 text-white"
                        : "bg-gray-100 text-gray-900"
                  }`}
                >
                  <span className="text-xs font-bold leading-none">
                    {dateNumber}
                  </span>
                  <span className="text-xs font-medium leading-none mt-0.5">
                    {dayAbbr}
                  </span>
                </div>

                {/* Appointment Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`text-xs font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {appointment.type}
                        </span>
                        <Badge
                          variant="secondary"
                          className={`text-xs ${
                            appointment.status === "Pending"
                              ? "border-orange-500 text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-600"
                              : appointment.status === "Started"
                                ? "border-blue-500 text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-600"
                                : appointment.status === "Completed"
                                  ? "border-green-500 text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400 dark:border-green-600"
                                  : appointment.status === "Cancelled"
                                    ? "border-red-500 text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 dark:border-red-600"
                                    : appointment.status === "Removed"
                                      ? "border-gray-500 text-gray-600 bg-gray-50 dark:bg-gray-900/20 dark:text-gray-400 dark:border-gray-600"
                                      : appointment.status === "Missed"
                                        ? "border-yellow-500 text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-600"
                                        : "border-gray-500 text-gray-600"
                          }`}
                        >
                          {appointment.status}
                        </Badge>
                        {isToday && (
                          <Badge
                            variant="secondary"
                            className="text-xs text-blue-700 bg-blue-100"
                          >
                            Today
                          </Badge>
                        )}
                      </div>
                      <p
                        className={`text-xs transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        {appointment.time}
                      </p>
                    </div>
                    {/* Start Consultation Icon Button - Show on Hover */}
                    <Button
                      onClick={() => {
                        if (!hasConsultationPermission) {
                          setShowPermissionMessage(
                            "You don't have permission to start this consultation",
                          );
                          setShowPermissionModal(true);
                          return; 
                        }

                        handleStartConsultation(appointment);
                      }}
                      variant="ghost"
                      size="sm"
                      className={`h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
                        isDarkMode
                          ? "text-blue-400 hover:bg-blue-900/20 hover:text-blue-300"
                          : "text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                      }`}
                      title="Start Consultation"
                    >
                      <Play className="w-4 h-4" />
                    </Button>

                    {/* Delete Icon Show On Hover */}
                    <Button
                      onClick={() => {
                        if (!hasConsultationPermission) {
                          setShowPermissionMessage(
                            "You don't have permission to delete this Appointment",
                          );
                          setShowPermissionModal(true);
                          return; 
                        }
                        onDeleteAppointmentID(appointment?.id || null);
                        showAppointmentDeleteModal(true);
                        return; 
                      }}
                      variant="ghost"
                      size="sm"
                       className={`h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
                      isDarkMode
                        ? "text-red-400 hover:bg-red-900/20 hover:text-red-300"
                        : "text-red-600 hover:bg-red-50 hover:text-red-700"
                    }`}
                      title="Delete Appointment"
                    >
                      <Trash className="w-4 h-4" />
                    </Button>

                    {/* Status and Days Until */}
                    <div className="flex flex-col items-end gap-1">
                      {!isToday && daysDiff > 0 && (
                        <Badge
                          variant="outline"
                          className={`text-xs ${
                            isDarkMode
                              ? "border-gray-600 text-gray-400"
                              : "border-gray-300 text-gray-500"
                          }`}
                        >
                          {daysDiff === 1 ? "1 day" : `${daysDiff} days`}
                        </Badge>
                      )}
                      {daysDiff < 0 && (
                        <Badge
                          variant="outline"
                          className="text-xs text-red-600 border-red-300"
                        >
                          Past due
                        </Badge>
                      )}
                    </div>
                  </div>

                  {appointment?.preConsultationTemplate && (
                    <div className="mt-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className={`w-full h-7 text-xs ${
                              isDarkMode
                                ? "border-green-700 text-green-400 hover:bg-green-900/20"
                                : "border-green-600 text-green-700 hover:bg-green-50"
                            }`}
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            Pre-consult completed
                          </Button>
                        </DialogTrigger>
                        <DialogContent
                          className={`max-w-2xl ${
                            isDarkMode ? "bg-gray-800 text-white" : "bg-white"
                          }`}
                        >
                          <DialogHeader>
                            <DialogTitle
                              className={
                                isDarkMode ? "text-white" : "text-gray-900"
                              }
                            >
                              Pre-Consultation Questionnaire
                            </DialogTitle>
                            <p
                              className={`text-sm ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              Completed on{" "}
                              {new Date(
                                appointment?.preConsultationTemplate?.createdOn,
                              ).toLocaleString()}
                            </p>
                          </DialogHeader>

                          <div className="space-y-6 mt-4 max-h-[60vh] overflow-y-auto pr-2">
                            {appointment?.preConsultationTemplate?.templateSections.map(
                              (section, idx) => {
                                const answered = section.userControls.filter(
                                  (q) => q.value !== null && q.value !== "",
                                );
                                if (answered.length === 0) return null;

                                return (
                                  <div
                                    key={idx}
                                    className={`p-4 rounded-lg ${
                                      isDarkMode ? "bg-gray-700" : "bg-gray-50"
                                    }`}
                                  >
                                    <h4
                                      className={`text-sm font-semibold mb-3 ${
                                        isDarkMode
                                          ? "text-white"
                                          : "text-gray-900"
                                      }`}
                                    >
                                      {section.sectionTitle}
                                    </h4>
                                    <div className="space-y-2">
                                      {answered.map(
                                        (q, qIdx) =>
                                          q.type !== "image" && (
                                            <div key={qIdx}>
                                              <p
                                                className={`text-sm font-medium ${
                                                  isDarkMode
                                                    ? "text-gray-300"
                                                    : "text-gray-800"
                                                }`}
                                              >
                                                {q.label}
                                              </p>
                                              <p
                                                className={`text-sm ${
                                                  isDarkMode
                                                    ? "text-gray-400"
                                                    : "text-gray-600"
                                                }`}
                                              >
                                                {q.value}
                                              </p>
                                            </div>
                                          ),
                                      )}
                                    </div>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination Controls */}
      {/* Pagination Controls */}
      {appointments.length > itemsPerPage && totalPages > 1 && (
        <div
          className={`flex items-center justify-between mt-4 pt-3 border-t ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div
            className={`text-xs ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, appointments.length)} of{" "}
            {appointments.length}
          </div>

          <div className="flex flex-wrap items-center max-w-full gap-1">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-1 rounded transition-colors duration-300 ${
                currentPage === 1
                  ? isDarkMode
                    ? "text-gray-600 cursor-not-allowed"
                    : "text-gray-400 cursor-not-allowed"
                  : isDarkMode
                    ? "text-gray-300 hover:bg-gray-700"
                    : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {(() => {
              const { start, end } = getVisiblePages(
                currentPage,
                totalPages,
                5,
              ); // max 5 visible

              return (
                <>
                  {start > 1 && (
                    <>
                      <button
                        onClick={() => handlePageChange(1)}
                        className="px-2 py-1 text-xs text-gray-600 rounded hover:bg-gray-100"
                      >
                        1
                      </button>
                      <span className="px-1 text-xs text-gray-400">…</span>
                    </>
                  )}

                  {Array.from(
                    { length: end - start + 1 },
                    (_, i) => start + i,
                  ).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        page === currentPage
                          ? isDarkMode
                            ? "bg-blue-600 text-white"
                            : "bg-blue-500 text-white"
                          : isDarkMode
                            ? "text-gray-300 hover:bg-gray-700"
                            : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  {end < totalPages && (
                    <>
                      <span className="px-1 text-xs text-gray-400">…</span>
                      <button
                        onClick={() => handlePageChange(totalPages)}
                        className="px-2 py-1 text-xs text-gray-600 rounded hover:bg-gray-100"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </>
              );
            })()}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`p-1 rounded transition-colors duration-300 ${
                currentPage === totalPages
                  ? isDarkMode
                    ? "text-gray-600 cursor-not-allowed"
                    : "text-gray-400 cursor-not-allowed"
                  : isDarkMode
                    ? "text-gray-300 hover:bg-gray-700"
                    : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
